import { Injectable } from '@angular/core';

/** The part of the DocuSign.js focused-view API the signing dialog uses. */
export interface DocuSignSigning {
  on(event: string, callback: (event: unknown) => void): void;
  mount(target: string | HTMLElement): void;
}

export interface DocuSignInstance {
  signing(options: { url: string; displayFormat: string }): DocuSignSigning;
}

interface DocuSignGlobal {
  loadDocuSign(integrationKey: string): Promise<DocuSignInstance>;
}

/**
 * `window.DocuSign`, read through a cast rather than a global `Window` augmentation: web-hr-packet declares its own,
 * and two augmentations with different types do not compile together.
 */
function docuSignGlobal(): DocuSignGlobal | undefined {
  return (window as unknown as { DocuSign?: DocuSignGlobal }).DocuSign;
}

/**
 * Loads the DocuSign.js bundle once per URL and hands out an initialised instance. The in-flight load is shared, so two
 * dialogs opened together never inject the script twice or miss its load event.
 */
@Injectable({ providedIn: 'root' })
export class DocuSignSdkLoader {
  /** How long the bundle may take to load before the load fails. */
  loadTimeoutMs = 30_000;

  private readonly loads = new Map<string, Promise<void>>();

  async load(
    scriptUrl: string,
    integrationKey: string
  ): Promise<DocuSignInstance> {
    await this.loadScript(scriptUrl);
    const docuSign = docuSignGlobal();
    if (!docuSign) {
      throw new Error('The DocuSign signing library did not load.');
    }
    return docuSign.loadDocuSign(integrationKey);
  }

  private loadScript(src: string): Promise<void> {
    if (docuSignGlobal()) {
      return Promise.resolve();
    }
    let load = this.loads.get(src);
    if (!load) {
      load = new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        // A blocked or stalled CDN fires neither load nor error; give up so the dialog can fall back to the redirect.
        const timer = setTimeout(
          () => fail('The DocuSign signing library took too long to load.'),
          this.loadTimeoutMs
        );
        const fail = (message: string) => {
          clearTimeout(timer);
          // Forget the failure so a later dialog can retry.
          this.loads.delete(src);
          script.remove();
          reject(new Error(message));
        };
        script.src = src;
        script.async = true;
        script.onload = () => {
          // A script that loads but defines no window.DocuSign (wrong URL, a proxy's error page) is a failure too;
          // cached as a success, every later dialog would fall back to the redirect until the page reloads.
          if (!docuSignGlobal()) {
            fail('The DocuSign signing library did not load.');
            return;
          }
          clearTimeout(timer);
          resolve();
        };
        script.onerror = () =>
          fail('The DocuSign signing library failed to load.');
        document.head.appendChild(script);
      });
      this.loads.set(src, load);
    }
    return load;
  }
}
